import { useCallback, useEffect, useRef, useState, type DependencyList } from "react";
import { ApiError } from "./api.js";

export interface QueryState<T> {
  data: T | null;
  error: string | null;
  loading: boolean;
  reload: () => Promise<void>;
  /** Replace the cached data locally (after a command returned the new entity). */
  set: (next: T | null | ((prev: T | null) => T | null)) => void;
}

/** Fetch-on-mount with reload. Stale responses are ignored when deps change mid-flight. */
export function useQuery<T>(fn: () => Promise<T>, deps: DependencyList, opts: { enabled?: boolean } = {}): QueryState<T> {
  const enabled = opts.enabled ?? true;
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(enabled);
  const seq = useRef(0);
  const fnRef = useRef(fn);
  fnRef.current = fn;

  const run = useCallback(async () => {
    const my = ++seq.current;
    setLoading(true);
    setError(null);
    try {
      const result = await fnRef.current();
      if (my === seq.current) setData(result);
    } catch (e) {
      if (my === seq.current) setError(errorMessage(e));
    } finally {
      if (my === seq.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!enabled) {
      setLoading(false);
      return;
    }
    void run();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, run, ...deps]);

  return {
    data,
    error,
    loading,
    reload: run,
    set: (next) => setData((prev) => (typeof next === "function" ? (next as (p: T | null) => T | null)(prev) : next)),
  };
}

export function errorMessage(e: unknown): string {
  if (e instanceof ApiError) {
    const p = e.problem;
    if (p.errors?.length) return `${p.detail ?? p.title}: ${p.errors.map((x) => x.message).join("; ")}`;
    return p.detail ?? p.title;
  }
  if (e instanceof TypeError) return "No se pudo conectar con el servidor. Revisa la conexión.";
  if (e instanceof Error) return e.message;
  return "Ocurrió un error inesperado.";
}

/** Debounced value for search boxes. */
export function useDebounced<T>(value: T, ms = 220): T {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return v;
}
